<script setup>
import { ref } from 'vue'
import { Head, router } from '@inertiajs/vue3'
import AppLayout from '@/Layouts/AppLayout.vue'
import { Button } from '@/Components/ui/button'

/**
 * Where an account lands when it has not confirmed its address yet.
 *
 * Deliberately not a dead end: everything on the site still works, and this only
 * stands between an unconfirmed account and the pages where money changes hands.
 */
defineProps({
  email: { type: String, default: '' },
  status: { type: String, default: '' },
})

const sending = ref(false)

const resend = () => {
  sending.value = true
  router.post('/email/verification-notification', {}, {
    preserveScroll: true,
    onFinish: () => (sending.value = false),
  })
}
</script>

<template>
  <Head><title>Confirm your email — Bindle</title></Head>

  <AppLayout>
    <div class="container-page py-16 md:py-24">
      <div class="mx-auto max-w-lg rounded-2xl border border-line bg-surface p-8 text-center">
        <span class="mx-auto mb-5 grid size-14 place-items-center rounded-full bg-brand-soft text-brand">
          <span class="material-symbols-outlined text-3xl">mark_email_unread</span>
        </span>

        <h1 class="text-2xl">Confirm your email address</h1>

        <p class="mt-3 text-[15px] text-muted">
          We have sent a link to <strong class="text-ink">{{ email }}</strong>. Opening it confirms the
          address is yours, which we ask for before you subscribe to a tutor.
        </p>

        <p v-if="status" class="mt-4 rounded-lg bg-success-bg px-4 py-3 text-sm font-semibold text-success">
          {{ status }}
        </p>

        <div class="mt-6 flex flex-col items-center gap-3">
          <Button size="lg" :disabled="sending" @click="resend">
            {{ sending ? 'Sending…' : 'Send the link again' }}
          </Button>
          <p class="text-sm text-muted">
            Check the spam folder before asking for another — they look alike to most mail providers.
          </p>
        </div>
      </div>
    </div>
  </AppLayout>
</template>
